import {
  useCallback,
  useRef,
  useEffect,
  RefObject,
  useMemo,
  memo,
} from "react";
import gsap from "gsap";
import { useVirtualList } from "../../hooks/useVirtualList";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";

export interface VirtualScrollerApi {
  scrollToIndex: (index: number, duration?: number) => void;
}
export interface RenderTileArgs {
  index: number;
  isFocused: boolean;
  x: number;
  y: number;
}
export interface VirtualScrollerProps {
  listIndex: number;
  virtualScrollerApiRef?: RefObject<VirtualScrollerApi | null>;
  listSize: number;
  initialIndex: number;
  easing: string;
  scrollDuration?: number;
  itemWidth: number;
  itemHeight: number;
  itemSizeAdjustment?: number;
  itemGap?: number;
  overscan?: number;
  viewportSize?: number;
  viewportCrossAxisSize?: number; // Size of the viewport in the cross-axis direction (height for horizontal, width for vertical)
  direction?: "vertical" | "horizontal";
  enableKeyboardNavigation?: boolean;
  enableRoamingFocus?: boolean;
  centerFocusedTile?: boolean;
  startPadding?: number; // Padding at the start of the carousel
  endPadding?: number; // Padding at the end of the carousel
  onIndexChange?: (index: number) => void; // Callback when the focused index changes
  renderTile: (props: RenderTileArgs) => React.ReactElement | null;
  // Function to get item size for each index (useful for variable-sized items)
  getItemSize?: (index: number) => { width: number; height: number };
}

export const VirtualScroller = memo(function VirtualScroller({
  virtualScrollerApiRef,
  listSize,
  initialIndex,
  easing,
  itemWidth,
  itemHeight,
  itemSizeAdjustment = 20,
  itemGap = 20,
  overscan = 1,
  direction = "horizontal",
  enableKeyboardNavigation = true,
  enableRoamingFocus = true,
  centerFocusedTile = false,
  startPadding = 0,
  endPadding = 0,
  onIndexChange,
  renderTile,
  viewportSize,
  viewportCrossAxisSize,
  listIndex,
  scrollDuration = 0.2,
  getItemSize,
}: VirtualScrollerProps) {
  // Calculate the main axis size based on direction
  // If getItemSize is provided, we'll use it dynamically
  const mainAxisItemSize = useMemo(
    () => (direction === "horizontal" ? itemWidth : itemHeight),
    [direction, itemWidth, itemHeight]
  );

  // Calculate item size function for VirtualListManager
  const itemSizeFunction = useMemo(() => {
    if (getItemSize) {
      // Variable size mode
      return (index: number) => {
        const size = getItemSize(index);
        const mainAxisSize =
          direction === "horizontal" ? size.width : size.height;
        return mainAxisSize + itemGap + itemSizeAdjustment;
      };
    } else {
      // Fixed size mode
      return mainAxisItemSize + itemGap + itemSizeAdjustment;
    }
  }, [getItemSize, direction, mainAxisItemSize, itemGap, itemSizeAdjustment]);

  const {
    virtualItems,
    containerRef,
    scrollRef,
    itemPositions,
    updateScroll,
    itemSizes,
  } = useVirtualList({
    itemCount: listSize,
    itemSize: itemSizeFunction,
    overscan,
    initialIndex: initialIndex,
    direction,
    viewportSize: viewportSize,
  });

  // Track current scroll position for roaming focus
  const currentScrollOffset = useRef<number>(0);

  // Cache for expensive scroll position calculations
  const scrollPositionCache = useRef<Map<number, number>>(new Map());
  const getOptimalScrollPosition = useCallback(
    (index: number) => {
      // Check cache first
      const cached = scrollPositionCache.current.get(index);
      if (cached !== undefined) {
        return cached;
      }

      if (!viewportSize || itemPositions[index] === undefined) return 0;

      // The actual tile position includes start padding
      const tileStart = itemPositions[index] + startPadding;

      // Get the actual visual item size in the main axis
      // If getItemSize is provided, use it; otherwise use mainAxisItemSize
      let actualTileSize: number;
      if (getItemSize) {
        const size = getItemSize(index);
        actualTileSize = direction === "horizontal" ? size.width : size.height;
      } else {
        actualTileSize = mainAxisItemSize;
      }

      if (centerFocusedTile) {
        const lastItemSize = itemSizes[listSize - 1] || 0;
        const totalContentSize =
          itemPositions[listSize - 1] +
          lastItemSize +
          startPadding +
          endPadding;
        const maxScrollPosition = Math.max(0, totalContentSize - viewportSize);
        const targetScrollPosition =
          tileStart - viewportSize / 2 + actualTileSize / 2;
        return Math.max(0, Math.min(targetScrollPosition, maxScrollPosition));
      }

      const tileEnd = tileStart + actualTileSize;

      const currentScrollStart = currentScrollOffset.current;
      const currentScrollEnd = currentScrollOffset.current + viewportSize;

      // Calculate the maximum scroll position (total content size minus viewport)
      const lastItemSize = itemSizes[listSize - 1] || 0;
      const totalContentSize =
        itemPositions[listSize - 1] + lastItemSize + startPadding + endPadding;
      const maxScrollPosition = Math.max(0, totalContentSize - viewportSize);

      // Check if tile is outside the visible area
      const visibleStart = currentScrollStart;
      const visibleEnd = currentScrollEnd;

      // If tile starts before the visible area (need to scroll left/up)
      if (tileStart < visibleStart) {
        // Position the tile so it starts at the left edge
        const targetScrollPosition = tileStart;
        const result = Math.max(0, targetScrollPosition);
        scrollPositionCache.current.set(index, result);
        return result;
      }

      // If tile ends after the visible area (need to scroll right/down)
      if (tileEnd > visibleEnd) {
        // Position the tile so it ends at the right edge
        const targetScrollPosition = tileEnd - viewportSize;
        const result = Math.min(maxScrollPosition, targetScrollPosition);
        scrollPositionCache.current.set(index, result);
        return result;
      }

      // Tile is already fully visible
      const result = currentScrollOffset.current;
      scrollPositionCache.current.set(index, result);
      return result;
    },
    [
      viewportSize,
      itemPositions,
      itemSizes,
      startPadding,
      endPadding,
      listSize,
      mainAxisItemSize,
      centerFocusedTile,
      getItemSize,
      direction,
    ]
  );

  const animateScrollToIndex = useCallback(
    (index: number, duration: number) => {
      const baseOffset = itemPositions[index] || 0;
      const targetOffset = enableRoamingFocus
        ? getOptimalScrollPosition(index)
        : baseOffset;

      // Calculate the maximum scroll position to respect end padding
      const lastItemSize = itemSizes[listSize - 1] || 0;
      const totalContentSize =
        itemPositions[listSize - 1] + lastItemSize + startPadding + endPadding;
      const maxScrollPosition = viewportSize
        ? Math.max(0, totalContentSize - viewportSize)
        : targetOffset;
      const clampedTargetOffset = Math.min(targetOffset, maxScrollPosition);

      // Don't animate if we don't need to scroll (roaming focus)
      if (
        enableRoamingFocus &&
        clampedTargetOffset === currentScrollOffset.current
      ) {
        return;
      }

      if (containerRef.current) {
        // Kill any existing animations on the container
        gsap.killTweensOf(containerRef.current);

        // Use provided duration or default
        const animDuration = duration;

        // Animate the container position
        gsap.to(containerRef.current, {
          [direction === "horizontal" ? "x" : "y"]: -clampedTargetOffset,
          duration: animDuration,
          ease: easing,
          onUpdate: () => {
            // Update the scroll offset during animation
            if (updateScroll) {
              const currentPos =
                containerRef.current?.[
                  direction === "horizontal" ? "x" : "y"
                ] || 0;
              const newOffset = -currentPos;
              currentScrollOffset.current = newOffset;
              updateScroll(newOffset);
            }
          },
          onComplete: () => {
            // Ensure final position is accurate
            currentScrollOffset.current = clampedTargetOffset;
          },
        });
      }
    },
    [
      itemPositions,
      itemSizes,
      containerRef,
      direction,
      easing,
      updateScroll,
      enableRoamingFocus,
      getOptimalScrollPosition,
      startPadding,
      endPadding,
      listSize,
      viewportSize,
    ]
  );

  // Use keyboard navigation hook
  const { currentIndex } = useKeyboardNavigation({
    itemCount: listSize,
    initialIndex,
    direction,
    onIndexChange: (index) => {
      // Always call the external callback
      onIndexChange?.(index);

      // Only scroll if roaming focus is disabled or tile is not fully visible
      if (!enableRoamingFocus || !isTileFullyVisible(index)) {
        animateScrollToIndex(index, scrollDuration);
      }
    },
    enabled: enableKeyboardNavigation,
  });

  // Initialize scroll offset tracking
  useEffect(() => {
    if (itemPositions[initialIndex] !== undefined) {
      let baseOffset: number;

      if (enableRoamingFocus) {
        baseOffset = getOptimalScrollPosition(initialIndex);
      } else {
        // For the initial index, we want to show it at the start with padding
        // So if initialIndex is 0, we want scroll offset to be 0 (showing startPadding)
        baseOffset = itemPositions[initialIndex];
      }

      // Calculate the maximum scroll position to respect end padding
      const lastItemSize = itemSizes[listSize - 1] || 0;
      const totalContentSize =
        itemPositions[listSize - 1] + lastItemSize + startPadding + endPadding;
      const maxScrollPosition = viewportSize
        ? Math.max(0, totalContentSize - viewportSize)
        : baseOffset;

      currentScrollOffset.current = Math.min(baseOffset, maxScrollPosition);

      // Set initial container position
      if (containerRef.current) {
        containerRef.current[direction === "horizontal" ? "x" : "y"] =
          -currentScrollOffset.current;
      }
    }
  }, [
    itemPositions,
    itemSizes,
    initialIndex,
    enableRoamingFocus,
    getOptimalScrollPosition,
    startPadding,
    endPadding,
    listSize,
    viewportSize,
    containerRef,
    direction,
  ]);

  // Check if a tile is fully visible in the viewport
  const isTileFullyVisible = useCallback(
    (index: number) => {
      if (!viewportSize || itemPositions[index] === undefined) return false;

      // The actual tile position includes start padding
      const tileStart = itemPositions[index] + startPadding;

      // Get the actual visual item size in the main axis
      let actualTileSize: number;
      if (getItemSize) {
        const size = getItemSize(index);
        actualTileSize = direction === "horizontal" ? size.width : size.height;
      } else {
        actualTileSize = mainAxisItemSize;
      }

      const tileEnd = tileStart + actualTileSize;

      // Calculate the visible area
      const visibleStart = currentScrollOffset.current;
      const visibleEnd = currentScrollOffset.current + viewportSize;

      // For the tile to be considered "fully visible", it needs to be:
      // 1. Completely within the viewport bounds
      // 2. Positioned correctly according to the optimal scroll position
      const isWithinViewport =
        tileStart >= visibleStart && tileEnd <= visibleEnd;

      // Get what the optimal scroll position should be for this tile
      const optimalScrollPosition = getOptimalScrollPosition(index);
      const isAtOptimalPosition =
        Math.abs(currentScrollOffset.current - optimalScrollPosition) < 1; // Allow 1px tolerance

      const isVisible = isWithinViewport && isAtOptimalPosition;

      return isVisible;
    },
    [
      viewportSize,
      itemPositions,
      startPadding,
      mainAxisItemSize,
      getOptimalScrollPosition,
      getItemSize,
      direction,
    ]
  );

  // Expose imperative API via ref prop
  useEffect(() => {
    if (virtualScrollerApiRef) {
      virtualScrollerApiRef.current = {
        scrollToIndex: (index: number, duration: number = scrollDuration) => {
          if (index < 0 || index >= listSize) {
            return;
          }
          animateScrollToIndex(index, duration);
        },
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateScrollToIndex, listSize]);

  // Clear scroll position cache when dependencies change
  useEffect(() => {
    scrollPositionCache.current.clear();
  }, [
    itemPositions,
    viewportSize,
    startPadding,
    endPadding,
    centerFocusedTile,
  ]);

  // Calculate cross-axis offset for centering tiles
  const crossAxisOffset = useMemo(() => {
    if (!viewportCrossAxisSize) return 0;

    // Get the tile size in the cross-axis direction
    const tileCrossAxisSize =
      direction === "horizontal" ? itemHeight : itemWidth;

    // Center the tile in the cross-axis
    return (viewportCrossAxisSize - tileCrossAxisSize) / 2;
  }, [direction, itemHeight, itemWidth, viewportCrossAxisSize]);

  const renderedTiles = useMemo(
    () =>
      virtualItems.map((item) =>
        renderTile({
          index: item.index,
          isFocused: item.index === currentIndex,
          x:
            direction === "horizontal"
              ? item.start + startPadding
              : crossAxisOffset,
          y:
            direction === "vertical"
              ? item.start + startPadding
              : crossAxisOffset,
        })
      ),
    [
      virtualItems,
      currentIndex,
      direction,
      startPadding,
      crossAxisOffset,
      renderTile,
    ]
  );

  // // Effect to manage texture caching based on keyboard navigation state and tile loading
  // useEffect(() => {
  //   const currentContainer = scrollRef.current;
  //   if (!currentContainer) {
  //     return;
  //   }

  //   // If keyboard navigation is enabled, disable texture caching
  //   if (enableKeyboardNavigation) {
  //     if (currentContainer?.isCachedAsTexture) {
  //       currentContainer.cacheAsTexture(false);
  //     }
  //     return;
  //   }

  //   // If keyboard navigation is disabled, enable texture caching after tiles load
  //   if (
  //     !enableKeyboardNavigation &&
  //     !currentContainer?.isCachedAsTexture
  //   ) {
  //     // Wait for tiles to fully render and textures to load
  //     const timeoutId = setTimeout(() => {
  //       if (
  //         scrollRef.current &&
  //         !scrollRef.current?.isCachedAsTexture
  //       ) {
  //         scrollRef.current.cacheAsTexture({
  //           resolution: 2,
  //           antialias: true,
  //         });
  //       }
  //     }, 150); // Delay to ensure tiles and their textures are loaded

  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [renderedTiles, enableKeyboardNavigation]); // Re-run when tiles change or keyboard nav toggles

  return (
    <pixiContainer
      ref={scrollRef}
      key={`virtual-scroller-${listIndex}`}
      label={`VirtualScroller[${listIndex}]`}
    >
      <pixiContainer
        ref={containerRef}
        x={0}
        y={0}
        label={`ScrollContainer[${listIndex}]`}
      >
        {renderedTiles}
      </pixiContainer>
    </pixiContainer>
  );
});
